import React from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { MatchAnalysis, pct } from '../../utils/matchAnalysis';
import { TEAM_LOGOS } from '../../assets/logos';
import TeamLogo from './TeamLogo';

interface MatchHeroProps {
  match: MatchAnalysis;
  league: string;
}

const MatchHero: React.FC<MatchHeroProps> = ({ match, league }) => {
  const { t } = useTranslation();
  const d = t.matchDashboard;
  const { winner, regulation } = match;
  const homeFav = winner.home >= winner.away;

  return (
    <section className="match-hero">
      {(['home', 'away'] as const).map((side) => {
        const logo = TEAM_LOGOS[league]?.[match[side]];
        return logo ? (
          <img
            key={side}
            className={`match-hero__ghost match-hero__ghost--${side}`}
            src={logo.src}
            alt=""
            aria-hidden="true"
          />
        ) : null;
      })}
      <div className="match-hero__top">
        <div className="match-hero__team">
          <TeamLogo team={match.home} league={league} size="lg" />
          <span className="match-hero__name">{match.home}</span>
          <span className="match-hero__role">{d.home}</span>
        </div>

        <div className="match-hero__center">
          {match.score ? (
            <>
              <span className="match-hero__score">
                <span className={homeFav ? 'match-hero__score-fav' : ''}>{match.score.home}</span>
                <span className="match-hero__colon">:</span>
                <span className={!homeFav ? 'match-hero__score-fav' : ''}>{match.score.away}</span>
              </span>
              <span className="match-hero__label">
                <span>{d.predictedScore}</span>
                <span>{pct(match.score.probability, 1)}</span>
              </span>
            </>
          ) : (
            <span className="match-hero__score match-hero__colon">vs</span>
          )}
          {match.expected && (
            <span className="match-hero__xg">
              <span>{d.expectedGoals}</span>
              <strong>
                {match.expected.home.toFixed(1)} – {match.expected.away.toFixed(1)}
              </strong>
            </span>
          )}
        </div>

        <div className="match-hero__team">
          <TeamLogo team={match.away} league={league} size="lg" />
          <span className="match-hero__name">{match.away}</span>
          <span className="match-hero__role">{d.away}</span>
        </div>
      </div>

      <div className="match-hero__bars">
        {!match.threeWay && (
          <div className="split-bar">
            <div className="split-bar__head">
              <span>{pct(winner.home, 1)}</span>
              <span className="split-bar__title">{d.winProbability}</span>
              <span>{pct(winner.away, 1)}</span>
            </div>
            <div className="split-bar__track">
              <span
                className={`split-bar__seg split-bar__seg--home${homeFav ? ' split-bar__seg--fav' : ''}`}
                style={{ flexGrow: winner.home }}
              />
              <span
                className={`split-bar__seg split-bar__seg--away${!homeFav ? ' split-bar__seg--fav' : ''}`}
                style={{ flexGrow: winner.away }}
              />
            </div>
          </div>
        )}

        <div className={`split-bar${match.threeWay ? '' : ' split-bar--thin'}`}>
          <div className="split-bar__head">
            <span>1 · {pct(regulation.home, match.threeWay ? 1 : 0)}</span>
            <span className="split-bar__title">
              {match.threeWay ? d.matchResult : d.regulation} · X {pct(regulation.draw, match.threeWay ? 1 : 0)}
            </span>
            <span>2 · {pct(regulation.away, match.threeWay ? 1 : 0)}</span>
          </div>
          <div className="split-bar__track">
            <span className="split-bar__seg split-bar__seg--home" style={{ flexGrow: regulation.home }} />
            <span className="split-bar__seg split-bar__seg--draw" style={{ flexGrow: regulation.draw }} />
            <span className="split-bar__seg split-bar__seg--away" style={{ flexGrow: regulation.away }} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default MatchHero;
